/** Low cover and obstacles (03 §2.1): what a crawling ranger hides behind and a vehicle cannot cross. */

import type { Pcg32 } from '../../sim/rng';
import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { capColor } from './snow';

const TAU = Math.PI * 2;

/** Sawn stump: temperate low cover. The pale cut face is the part that reads from above. */
export const stump: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const h = rng.range(0.56, 0.72);
  mb.cylinder(0.46, 0.38, h, 7, p.color('timber.mid'), place(0, 0, 0, rng.range(0, TAU)), capColor(p, 'timber.hi'));
  // Two root flares break the circle, so it is not mistaken for a barrel.
  const a = rng.range(0, TAU);
  for (const da of [0, 2.3]) {
    const flare = { all: p.color('timber.lo'), top: p.color('timber.mid') };
    mb.box(0.3, 0.2, 0.34, flare, place(Math.sin(a + da) * 0.36, 0, Math.cos(a + da) * 0.36, a + da));
  }
  return mb;
};

/**
 * Hedgerow along x. A drawn hedge has a wobbly top line, so the run is sections of uneven
 * height and depth with a clump on each; a single long box would read as a green wall.
 */
export function hedgeRun(p: Palette, rng: Pcg32, length = 6): MeshBuilder {
  const mb = new MeshBuilder();
  const n = Math.max(1, Math.round(length / 2));
  const seg = length / n;
  const end = p.color('foliage.lo');
  const leaf = { all: p.color('foliage.mid'), top: p.color('foliage.hi'), left: end, right: end };
  for (let i = 0; i < n; i++) {
    const x = -length / 2 + seg * (i + 0.5);
    const h = rng.range(0.92, 1.18);
    const d = rng.range(0.84, 1.0);
    mb.box(seg, h, d, leaf, place(x, 0, rng.range(-0.04, 0.04)));
    // Clumps: foliage, or in the arctic a cushion of snow on every other section only.
    const snowy = p.biome === 'arctic' && i % 2 === 0;
    const clump = p.color(snowy ? 'ground.3' : 'foliage.hi');
    const cx = x + rng.range(-0.2, 0.2) * seg;
    // Not turned: a clump lying across the run would bulge past the half-metre the sim blocks.
    mb.blob(seg * 0.36, 0.3, d * 0.42, clump, place(cx, h - 0.06, 0), { detail: 0, jitter: 0.12, rng });
  }
  return mb;
}

/**
 * Wind-packed snow drift; in other biomes the same shape in their ground ramp is a dune
 * hummock or a grassy bank. The body sits a step darker than open ground and only the crest
 * takes the lightest tone - white on white would be invisible.
 */
export const snowDrift: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const yaw = rng.range(0, TAU);
  const lee = p.color('ground.1');
  const crest = p.color('ground.3');
  mb.blob(1.3, 0.88, 1.12, lee, place(0, 0.08, 0, yaw), { jitter: 0.1, rng, topColor: crest, flatBottom: true });
  const a = yaw + rng.range(1.2, 2.0);
  const lump = place(Math.sin(a) * 0.7, 0.04, Math.cos(a) * 0.7);
  mb.blob(0.5, 0.4, 0.5, lee, lump, { detail: 0, topColor: crest, flatBottom: true });
  return mb;
};

/**
 * Czech hedgehog: three steel beams, each 55 degrees off vertical and 120 degrees apart, which
 * is exactly three mutually perpendicular bars stood on their ends.
 */
export const tankTrap: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const yaw = rng.range(0, TAU);
  const beam = { all: p.color('metal.lo'), top: capColor(p, 'metal.mid') };
  for (let i = 0; i < 3; i++) {
    mb.with(place(0, 0.54, 0, yaw + (i / 3) * TAU), () => {
      mb.box(0.2, 1.7, 0.2, beam, place(0, 0, 0, 0, 1, 0.96), true);
    });
  }
  return mb;
};
